import { FC, ReactNode } from "react";
import { Navigate } from "react-router";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@mui/material";

import { Permission } from "./permissions";
import { useAuth } from "./useAuth";

/**
 * Gate a route or a section on a permission.
 *
 * Three states, deliberately handled differently:
 *
 *  - No session          -> redirect to login.
 *  - Server hasn't answered yet -> spinner. Rendering "Acces refuse" while the
 *                          permission list is still loading would flash a lie
 *                          on every page load.
 *  - Session, not authorised -> explain IN PLACE. Redirecting an
 *                          authenticated-but-unauthorised operator looks like
 *                          the app is broken or like they were logged out, and
 *                          hides the reason.
 */
interface Props {
  permission?: Permission | string;
  /**
   * Alternative to `permission`: holding ANY of these opens the section.
   * Needed where one screen aggregates several capabilities - the billing page
   * is reachable by a pricing analyst and by a promo-code manager, who share
   * no single key.
   */
  anyOf?: (Permission | string)[];
  children: ReactNode;
  fallback?: ReactNode;
}

export const RequirePermission: FC<Props> = ({
  permission,
  anyOf,
  children,
  fallback,
}) => {
  const { canAny, isResolved, user } = useAuth();
  const hasToken = !!localStorage.getItem("token");

  const required = anyOf ?? (permission ? [permission] : []);
  const allowed = required.length === 0 ? true : canAny(required);

  if (!hasToken && !user) {
    return <Navigate to="/" replace />;
  }

  if (!isResolved && !allowed) {
    return <PermissionLoading />;
  }

  if (!allowed) {
    return <>{fallback ?? <AccessDenied permission={required[0]} />}</>;
  }

  return <>{children}</>;
};

const PermissionLoading: FC = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 320,
    }}
    aria-busy="true"
  >
    <CircularProgress size={28} />
  </div>
);

/** Default denial panel. Deliberately plain - no app chrome, no dead links. */
export const AccessDenied: FC<{ permission?: Permission | string }> = ({
  permission,
}) => {
  const { t } = useTranslation();

  return (
    <div
      role="alert"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        minHeight: 320,
        padding: 32,
        textAlign: "center",
      }}
    >
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
        {t("auth.accessDeniedTitle", "Acces refuse")}
      </h2>
      <p style={{ margin: 0, maxWidth: 460, lineHeight: 1.5, color: "#616161" }}>
        {t(
          "auth.accessDeniedBody",
          "Votre compte n'a pas les droits necessaires pour cette section. Communiquez la reference ci-dessous a un administrateur pour obtenir l'acces."
        )}
      </p>
      {permission && (
        <code style={{ fontSize: 12, color: "#9e9e9e" }}>{permission}</code>
      )}
    </div>
  );
};

export default RequirePermission;
